import type { FormValues } from '../components/PropertyForm';

export interface SavedEntry {
  ts: number;
  position: [number, number];
  values: FormValues;
  result: {
    predictedPrice: number;
    priceRange: { low: number; high: number };
    municipalityName: string;
    districtName: string | null;
    nearestStation: { name: string; distanceM: number } | null;
  };
}

const HISTORY_KEY = 'mapkachi.history.v1';
const COMPARE_KEY = 'mapkachi.compare.v1';
const HISTORY_MAX = 20;
const COMPARE_MAX = 3;

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, val: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(val));
  } catch {
    // quota exceeded or storage disabled -- silently ignore
  }
}

export function loadHistory(): SavedEntry[] {
  return readJson<SavedEntry[]>(HISTORY_KEY) ?? [];
}

export function pushHistory(entry: SavedEntry): SavedEntry[] {
  const next = [entry, ...loadHistory()].slice(0, HISTORY_MAX);
  writeJson(HISTORY_KEY, next);
  return next;
}

export function removeHistory(ts: number): SavedEntry[] {
  const next = loadHistory().filter((e) => e.ts !== ts);
  writeJson(HISTORY_KEY, next);
  return next;
}

export function clearHistory(): void {
  writeJson(HISTORY_KEY, []);
}

export function loadCompare(): SavedEntry[] {
  return readJson<SavedEntry[]>(COMPARE_KEY) ?? [];
}

export function addToCompare(entry: SavedEntry): { items: SavedEntry[]; full: boolean } {
  const current = loadCompare();
  if (current.some((e) => e.ts === entry.ts)) return { items: current, full: false };
  if (current.length >= COMPARE_MAX) return { items: current, full: true };
  const next = [...current, entry];
  writeJson(COMPARE_KEY, next);
  return { items: next, full: false };
}

export function removeFromCompare(ts: number): SavedEntry[] {
  const next = loadCompare().filter((e) => e.ts !== ts);
  writeJson(COMPARE_KEY, next);
  return next;
}

export function clearCompare(): void {
  writeJson(COMPARE_KEY, []);
}

export const HISTORY_LIMIT = HISTORY_MAX;
export const COMPARE_LIMIT = COMPARE_MAX;
